import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PrimaryButton, Button } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';

import { APP_URLS } from 'src/constants';
import { useAuth, useType } from 'src/contexts';
import { Typography } from 'src/components';
import { ISummary, TAIForm, IAIUpdateRequest } from 'src/types';
import { useToasts } from 'src/hooks';
import { flush, loadSummaries, getFile } from 'src/services/schoolRequest';
import { UpdateForm } from '../../components/school';
import SummarySearch from '../../components/admin/SummarySearch';
import { DiUpdateForm } from 'src/di/components';
import { IDIRequest } from 'src/types/org/di/diRequestSchema';
import { IData } from 'src/types/org/common/dataSchema';

let loading = false;
let progress: string = 'Retrieving Records';


const LoadProgress = (props: any) => {
  const [message, setMessage] = useState(props.message || '');

  useEffect(() => {
    setMessage(props.message);
  }, [props.message]);

  return (
    <div>
      {message &&
        <Grid item xs={12}>
          <CircularProgress /> {message}
        </Grid>
      }
    </div>
  );
};

interface AdminDashboardProps {
  type: 'ai' | 'di';
}

interface IRecordView {
  summary: ISummary;
  request: IDIRequest<IData>;
}

//shared between ai and di
const AdminDashboard: FC<AdminDashboardProps> = ({ type }) => {
  const { userName, firstName } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ISummary[]>([]);
  const { render: renderToasts, toastErrorMessage } = useToasts();
  const [viewing, setViewing] = useState<IAIUpdateRequest | undefined>();
  const [viewingDI, setViewingDI] = useState<IRecordView | undefined>(undefined);


  const config = type === 'ai' ? {
    disableViewer: false,
    filters: ['ingestionPath'],
    statuses: []
  } : { //di
    filters: [],
    disableViewer: false,
    statuses: [
      'submitted',
      'processing',
      'reopen',
      'qcReadyVetting',
      'qcReadyReopen',
      'qcReadyRejected',
      'rejected',
      'deactivationApproval',
      'xpmDenied',
      'opsApproved',
      'completed',
    ]
  }

  const { type: contextType, setType } = useType();

  const handleViewRequest = useCallback(
    async (data: ISummary,
          filters: {
            search:string; 
            requestType: string; 
            ingestionPathCd?: string },
    ) => {

      try {
        if (!data.submission)
          throw new Error("Cannot retrieve a non-existent filePath.");

        setType(data.recordCategoryType ?? '');

        const { search, requestType, ingestionPathCd } = filters;
        const navState: any = {
          search,
          requestType,
        };
        if (data.recordCategoryType === "ai" && ingestionPathCd !== undefined && ingestionPathCd !== null) {
          navState.ingestionPathCd = ingestionPathCd.toString();
        }

        if (data.recordCategoryType === 'di' && data.requestType === 'create') {
          navigate(APP_URLS.DIFORM, {
            state: {
              summary: data,
              type: 'di', ...navState
            }
          });
        } else if (data.recordCategoryType === 'di' && data.requestType === 'update') {
          if (!data.submission) throw new Error("No submission could be located.");
          const update: IDIRequest<IData> = await getFile(data.submission);
          if (!update) throw new Error("Cannot find request");
          setViewingDI({
            summary: data,
            request: update,
            _filters: {
              search,
              requestType,
              ingestionPathCd: ingestionPathCd?.toString() ?? "all"
            }
          } as {
            summary: ISummary;
            request: IDIRequest<IData>;
            _filters: {
              search: string;
              requestType: string;
              ingestionPathCd: string;
            };
          });
        } else {
          if (data.appliedType === 'aia' && data.requestType === 'create') {
            navigate(APP_URLS.FORM, {
              state: {
                summary: data,
                type: 'ai',...navState
              }
            });
          } else if (data.requestType === 'create') {
            const aiForm: TAIForm = await getFile(data.submission);
            navigate(APP_URLS.SCHOOL, {
              state: {
                summary: data,
                file: data?.draft ||data?.submission,
                type: 'ai',
                ...navState,
              },
            });
          } else {
            const update: IAIUpdateRequest = await getFile(data.submission);
            //Temporarily attaching filters to the update request to persist or hold values of search and filters selected
            setViewing({
              ...(update as IAIUpdateRequest),
              _filters: {
                search,
                requestType,
                ingestionPathCd: ingestionPathCd?.toString() ?? "all"
              }
            } as IAIUpdateRequest & {
              _filters: {
                search: string;
                requestType: string;
                ingestionPathCd: string;
              };
            });
          }
        }
      } catch (error: any) {
        toastErrorMessage(error.message);
      }
    },
    [navigate],
  );

  const handleUpdateRequest = (request: ISummary) => {
    init();
  };

  const handleReload = () => {
    flush();
    init();
  };

  const handleRefresh = () => {
    init();
  };

  const init = () => {
    (async () => {
      if (loading) return;
      loading = true;
      progress = 'Loading...';
      setRequests([]);
      const records = await loadSummaries({ type });
      loading = false;
      progress = '';
      setRequests(records.all());
    })();
  };

  useEffect(() => {
    (async () => {
      init();
    })();
  }, []);

  return (<div>
    <Grid container spacing={2}>
      {viewing || viewingDI ?
        <Grid item xs={12}>
          {/* update form */}
          {type === 'ai' && viewing &&          
          (<UpdateForm  
            readOnly={true} 
            request={viewing} 
            onCancel={() => {

            const filters = (viewing as IAIUpdateRequest & {
              _filters: {
                search: string;
                requestType: string;
                ingestionPathCd: string;
              };
            })._filters;
  
              if  (filters) {
                const { search, requestType, ingestionPathCd } = filters;
                setViewing(undefined);

                navigate(APP_URLS.HOME, {
                  state: {
                    type: 'ai',
                    search,
                    requestType,
                    ingestionPathCd:ingestionPathCd.toString() || 'all',
                  },
                });
            
              } else {
                setViewing(undefined);
              }
            }} 
          />)
          }
          {type === 'di' && viewingDI && (
          <DiUpdateForm
            readOnly={true}
            request={viewingDI.request}
            onCancel={() => {
            const filters = (viewingDI as {
              summary: ISummary;
              request: IDIRequest<IData>;
              _filters?: {
                search: string;
                requestType: string;
                ingestionPathCd: string;
              };
            })._filters;

            if (filters) {
              const { search, requestType, ingestionPathCd } = filters;
              setViewingDI(undefined);

              navigate(APP_URLS.DIHOME, {
                state: {
                  type: 'di',
                  search,
                  requestType,
                  ingestionPathCd: ingestionPathCd.toString() || 'all',
                },
              });
            } else {
              setViewingDI(undefined);
            }
          }}
        />
      )}
        </Grid> :
        <>
          {renderToasts()}
          <Grid item xs={12}>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">Welcome, {firstName}</Typography>

            <LoadProgress message={progress}></LoadProgress>
            {!progress &&
              <div>
                <button className='cb-btn cb-btn-primary' style={{ marginRight: '10px' }} onClick={handleRefresh}>Refresh</button>
                <button className='cb-btn' style={{ marginRight: '10px', backgroundColor: "#FFF" }} onClick={handleReload}>Hard Reload</button>
                {type === 'di' &&
                  <button className='cb-btn cb-btn-primary' onClick={() => navigate(APP_URLS.SEARCH, {
                    state: {
                      type: 'di'
                    },
                  })}> Score Send Code Search</button>
                }
                {/* AI Search */}
                {type === 'ai' &&
                  <button className='cb-btn cb-btn-primary' onClick={() => navigate(APP_URLS.SEARCH, {
                    state: {
                      type: 'ai'
                    },
                  })}>School Code Search</button>
                }
              </div>
            }
          </Grid>
          <Grid item xs={12}>
            <SummarySearch data={requests}
              statuses={config.statuses}
              filters={config.filters}
              disableViewer={config.disableViewer}
              type={type}
              onViewRequest={(data, filters)=>handleViewRequest(data, filters)}
              onUpdateRequest={handleUpdateRequest}
            />
          </Grid>
        </>
      }
    </Grid>
  </div>);
};

export default AdminDashboard;
